import { Box } from '@zoopla-eng/zds/box';
import type { BreadcrumbsProps } from '@zoopla-eng/zds/breadcrumbs';
import { Breadcrumbs } from '@zoopla-eng/zds/breadcrumbs';
import {
    AdvertProvider,
    AdvertSlotDfp,
    ADBRIDG_CLASS,
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    AD_UNIT_SECTION_NEW,
    AD_UNIT_PAGE_TYPE,
    DfpTargeting
} from '@zoopla/adverts/dfp';
import { AdSideBarLayout } from '@zoopla/layout/page';
import { ReactElement, useEffect, useState } from 'react';
import { stickyStyle } from './Chrome.styles.css';
import SeoSideLinks from './components/SeoSideLinks/SeoSideLinks';

type SeoLinksType = {
    areaName: string;
    slug: string;
};

const Adverts = ({ seoSideLinks }: { seoSideLinks?: SeoLinksType }) => (
    <>
        <AdvertSlotDfp
            position={0}
            showOnly={['>', 960]}
            sizes={[[300, 16], 'fluid']}
            adUnitCode={[
                AD_UNIT_ROOTS.TEXT_LINK,
                AD_UNIT_SECTION.HOUSE_PRICES,
                '/House-Prices'
            ]}
            adbridgClass={ADBRIDG_CLASS.TEXT_LINK}
            adbridgDataClass={ADBRIDG_CLASS.ADB_TEXT_LINK}
        />
        <AdvertSlotDfp
            position={1}
            showOnly={['>', 960]}
            sizes={[[300, 250]]}
            adUnitCode={[
                AD_UNIT_ROOTS.MPU,
                AD_UNIT_SECTION.HOUSE_PRICES,
                '/House-Prices'
            ]}
            adbridgClass={ADBRIDG_CLASS.MPU}
        />
        {seoSideLinks ? <SeoSideLinks {...seoSideLinks} /> : null}
        <div className={stickyStyle}>
            <AdvertSlotDfp
                position={2}
                showOnly={['>', 960]}
                sizes={[[300, 600]]}
                adUnitCode={[
                    AD_UNIT_ROOTS.SKY_SCRAPER,
                    AD_UNIT_SECTION.HOUSE_PRICES,
                    '/House-Prices'
                ]}
                adbridgClass={ADBRIDG_CLASS.SKYSCRAPER}
            />
        </div>
    </>
);

const adBridgPage = AD_UNIT_SECTION.HOUSE_PRICES;
const adBridgPageNew = AD_UNIT_SECTION_NEW.HOUSE_PRICES;
const pageType =
    AD_UNIT_PAGE_TYPE.LISTINGS_SEARCH_RESULT_PAGE; /** as agreed with Ad-ops to limit the amount of page types */
const targetingDefaults = {
    activity: 'house_prices_landing',
    page: '/house-prices/browse/*'
};

/**
 * Default layout used across site, includes web chrome i.e header, footer etc
 */
export const Layout = ({
    breadcrumbs,
    dfpAdTargeting,
    isPending,
    pageNumber,
    children,
    seoSideLinks
}: {
    breadcrumbs?: BreadcrumbsProps['items'];
    dfpAdTargeting: DfpTargeting;
    isPending: boolean;
    pageNumber?: number;
    children: ReactElement;
    seoSideLinks?: SeoLinksType;
}) => {
    const [advertProviderContext, updateAdvertProviderContext] = useState({
        targeting: {
            ...targetingDefaults,
            ...dfpAdTargeting
        },
        pageNumber,
        adBridgPage,
        adBridgPageNew,
        pageType,
        delayInit: isPending
    });

    useEffect(() => {
        updateAdvertProviderContext({
            targeting: {
                ...targetingDefaults,
                ...dfpAdTargeting
            },
            pageNumber,
            adBridgPage,
            adBridgPageNew,
            pageType,
            delayInit: isPending
        });
    }, [dfpAdTargeting, pageNumber, isPending]);

    return (
        <AdSideBarLayout
            adverts={
                <AdvertProvider value={advertProviderContext}>
                    <Adverts seoSideLinks={seoSideLinks} />
                </AdvertProvider>
            }
            main={
                <>
                    {breadcrumbs && (
                        <Box
                            fw
                            py="S200"
                            pl="S400"
                            bpS200={{ pl: 0 }}
                            display="none"
                            bpM100={{ display: 'block' }}
                        >
                            <Breadcrumbs items={breadcrumbs} />
                        </Box>
                    )}
                    {children}
                    {breadcrumbs && (
                        <Box
                            fw
                            pl="S400"
                            pt="L100"
                            bpS200={{ pl: 0 }}
                            bpM100={{ display: 'none' }}
                        >
                            <Breadcrumbs items={breadcrumbs} />
                        </Box>
                    )}
                    {seoSideLinks && (
                        <Box
                            fw
                            px="S400"
                            pt="M100"
                            bpS200={{ px: 0 }}
                            bpL100={{ display: 'none' }}
                        >
                            <SeoSideLinks {...seoSideLinks} />
                        </Box>
                    )}
                </>
            }
        />
    );
};
